'use client';

import classnames from 'classnames';
import type { MouseEvent, ReactNode } from 'react';
import { useDialog } from './use-dialog';
import styles from './DialogOverlay.module.scss';

interface DialogOverlayProps {
    /** Names the dialog for assistive technology. */
    label: string;
    /** Called on Escape. Without it, Escape does nothing. */
    onEscape?: () => void;
    /** Called on a click on the backdrop around the dialog. */
    onBackdropClick?: () => void;
    /** Extra classes on the backdrop, e.g. its stacking layer. */
    className?: string;
    /** Extra classes on the dialog box. */
    dialogClassName?: string;
    children: ReactNode;
}

/**
 * A screen-covering backdrop with a modal dialog centered on it, with the
 * keyboard behavior of `useDialog`: focus in and back out, Escape, and Tab
 * kept inside.
 */
export function DialogOverlay({ label, onEscape, onBackdropClick, className, dialogClassName, children }: DialogOverlayProps): JSX.Element {
    const { ref, onKeyDown } = useDialog<HTMLDivElement>(onEscape);

    const handleBackdropMouseDown = (event: MouseEvent<HTMLDivElement>): void => {
        // The backdrop can't take focus: keep it in the dialog.
        if (event.target === event.currentTarget) {
            event.preventDefault();
        }
    };

    const handleBackdropClick = (event: MouseEvent<HTMLDivElement>): void => {
        if (event.target === event.currentTarget) {
            onBackdropClick?.();
        }
    };

    return (
        <div className={classnames(styles.overlay, className)} onMouseDown={handleBackdropMouseDown} onClick={handleBackdropClick}>
            <div
                ref={ref}
                role="dialog"
                aria-modal="true"
                aria-label={label}
                tabIndex={-1}
                className={classnames(styles.dialog, dialogClassName)}
                onKeyDown={onKeyDown}
            >
                {children}
            </div>
        </div>
    );
}
